import { create } from 'zustand/react';
import { ProductCardState } from './ProductCardState';
import { MgPackageType } from '@retail/products/types';
import {
  calculateCoverageFromSalesFactor,
  calculateNewSalesFactor,
  calculateSalesCoverageFromCostAndSalesPrice,
  calculateSalesFactorFromDG,
  roundToMaxDecimalCount,
  STANDARD_VAT_FACTOR,
} from '@retail/utils';

const MAX_SALES_FACTOR_DECIMALS = 6;

const initState: Pick<ProductCardState, 'selectedPackageType' | 'editingField' | 'newMarkup'> = {
  selectedPackageType: 'PRIMARY',
  editingField: undefined,
  newMarkup: undefined,
};

export const useProductCardStore = create<ProductCardState>((set) => ({
  ...initState,

  changeSelectedPackage: (selectedPackageType: MgPackageType) =>
    set({ selectedPackageType, newMarkup: undefined, editingField: undefined }),

  getSalesPriceEditState: (noCalculation: boolean, noCostPrice: boolean) => {
    if (noCalculation) {
      return 'noCalculationForPackage';
    }
    if (noCostPrice) {
      return 'noCostPrice';
    }
    return 'editable';
  },

  setEditingField: (field) => set({ editingField: field }),
  closeEditingField: () => set({ editingField: undefined }),

  setNewSalesFactor: (newSalesFactor, costPriceIncVat, costPriceExVat) => {
    const newSalesCoverage = calculateCoverageFromSalesFactor(newSalesFactor);

    const newSalesPrice = costPriceExVat && costPriceExVat * newSalesFactor;
    const newSalesPriceIncVat = costPriceIncVat && costPriceIncVat * newSalesFactor;

    return set({
      newMarkup: {
        salesFactor: roundToMaxDecimalCount(newSalesFactor, MAX_SALES_FACTOR_DECIMALS),
        salesCoverage: roundToMaxDecimalCount(newSalesCoverage, MAX_SALES_FACTOR_DECIMALS),
        salesPrice: newSalesPrice,
        salesPriceIncVat: newSalesPriceIncVat,
        editedField: 'factor',
      },
      editingField: undefined,
    });
  },

  setNewSalesCoverage: (newSalesCoverage, costPriceIncVat, costPriceExVat) => {
    const newSalesFactor = calculateSalesFactorFromDG(newSalesCoverage);

    const newSalesPrice = costPriceExVat && costPriceExVat * newSalesFactor;
    const newSalesPriceIncVat = costPriceIncVat && costPriceIncVat * newSalesFactor;

    return set({
      newMarkup: {
        salesCoverage: roundToMaxDecimalCount(newSalesCoverage, MAX_SALES_FACTOR_DECIMALS),
        salesFactor: roundToMaxDecimalCount(newSalesFactor, MAX_SALES_FACTOR_DECIMALS),
        salesPrice: newSalesPrice,
        salesPriceIncVat: newSalesPriceIncVat,
        editedField: 'coverage',
      },
      editingField: undefined,
    });
  },

  setNewSalesPrice: (newSalesPrice, includeVat, currentCostPrice) => {
    if (!currentCostPrice) {
      throw new Error('Cannot set new sales price without having a cost price');
    }
    const vatFactor = STANDARD_VAT_FACTOR;
    const newSalesCoverage = calculateSalesCoverageFromCostAndSalesPrice(
      currentCostPrice,
      newSalesPrice
    );
    const newSalesFactor = calculateNewSalesFactor(currentCostPrice, newSalesPrice);

    return set({
      newMarkup: {
        salesPrice: includeVat ? newSalesPrice / vatFactor : newSalesPrice,
        salesPriceIncVat: includeVat ? newSalesPrice : newSalesPrice * vatFactor,
        editedField: 'price',
        salesCoverage: roundToMaxDecimalCount(newSalesCoverage, 6),
        salesFactor: roundToMaxDecimalCount(newSalesFactor, 6),
      },
      editingField: undefined,
    });
  },

  resetMarkup: () => set({ newMarkup: undefined, editingField: undefined }),

  resetState: () => set(initState),
}));
